import React, { useEffect, useState } from "react";
import { HOTMART_URL } from "../../lib/constants";
import { ShieldCheck, Zap, ArrowRight } from "lucide-react";

const HERO_BG =
  "https://customer-assets.emergentagent.com/job_tower-crane-pro/artifacts/zhvkl94x_Design%20sem%20nome%20%282%29.png";

function useCountdown(initialSeconds) {
  const [seconds, setSeconds] = useState(initialSeconds);
  useEffect(() => {
    const id = setInterval(() => {
      setSeconds((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const h = String(Math.floor(seconds / 3600)).padStart(2, "0");
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
  const s = String(seconds % 60).padStart(2, "0");
  return { h, m, s };
}

export default function Hero() {
  const { h, m, s } = useCountdown(4 * 3600 + 36 * 60 + 7);

  return (
    <section
      className="relative min-h-[100vh] flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
      />
      {/* Subtle 30% gradient for text legibility on left */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent"
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 grain"
      />

      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-20 border-b border-white/10 bg-black/40 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-yellow-400 flex items-center justify-center">
              <span className="text-black font-black text-lg">K</span>
            </div>
            <span className="font-display text-xl tracking-tight">KAWAN GRUISTA</span>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-widest text-yellow-400">
            <span className="w-2 h-2 bg-yellow-400 rounded-full pulse-dot" />
            Turma aberta
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 w-full grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="inline-flex items-center gap-2 mb-6 border border-yellow-400/40 bg-yellow-400/5 px-3 py-1.5 text-xs uppercase tracking-[0.2em] text-yellow-400 font-semibold">
            <ShieldCheck className="w-3.5 h-3.5" />
            SIGO · Passaporte Qualifica · Formação Teórica Certificada
          </div>
          <h1 className="font-display text-[56px] leading-[0.9] sm:text-7xl md:text-8xl lg:text-[7.5rem] text-white">
            MANOBRADOR
            <br />
            <span className="text-yellow-400">DE GRUA</span>
            <br />
            TORRE.
          </h1>
          <p className="mt-6 max-w-xl text-base sm:text-lg text-zinc-300 leading-relaxed">
            A única formação online com certificação via{" "}
            <span className="text-white font-semibold">SIGO e Passaporte Qualifica</span>.
            Começa hoje. <span className="text-yellow-400 font-bold">Trabalha amanhã.</span>
          </p>

          {/* Price block */}
          <div className="mt-8 flex flex-wrap items-end gap-x-6 gap-y-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">Preço normal</div>
              <div className="text-2xl font-display text-zinc-500 line-through">279€</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-yellow-400">Hoje</div>
              <div className="font-display text-7xl sm:text-8xl leading-none text-white">
                <span className="text-yellow-400">€</span>147
              </div>
            </div>
          </div>
          <p className="mt-2 text-sm text-zinc-400">
            Pagamento único · Acesso imediato por 1 ano · Certificação SIGO incluída
          </p>
          <p className="mt-1 text-xs text-zinc-500">
            Só queres o conteúdo?{" "}
            <a href="#oferta" className="text-yellow-400/80 hover:text-yellow-400 underline underline-offset-2">
              Pacote Essencial por €37 + IVA (sem certificação)
            </a>
          </p>

          {/* Urgency line */}
          <p className="mt-6 text-xs uppercase tracking-widest text-zinc-500 flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-yellow-400 pulse-dot" />
            Próxima turma de certificação aberta · Inscrições por ordem de chegada
          </p>

          {/* CTA */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <a
              href={HOTMART_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-button"
              className="group inline-flex items-center justify-center gap-3 bg-yellow-400 hover:bg-yellow-300 text-black font-display tracking-wide text-lg sm:text-xl px-8 py-5 transition-colors"
            >
              QUERO O MEU CARTÃO SIGO
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </a>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs uppercase tracking-widest text-zinc-400">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-yellow-400" /> Garantia 14 dias
            </span>
            <span className="text-zinc-600">·</span>
            <span>Pagamento seguro Hotmart</span>
            <span className="text-zinc-600">·</span>
            <span>MBWay · Multibanco · Cartão</span>
          </div>
        </div>

        {/* Right column: stacked proof cards */}
        <div className="lg:col-span-5 hidden lg:flex flex-col gap-4">
          <ProofCard big="+1.900" label="Alunos formados" />
          <ProofCard big="+50" label="Aulas gravadas" />
          <ProofCard big="SIGO" label="Certificação oficial" />
          <ProofCard big="14d" label="Garantia total" />
        </div>
      </div>
    </section>
  );
}

function ProofCard({ big, label }) {
  return (
    <div className="border border-white/10 bg-black/50 backdrop-blur-md p-5 flex items-baseline justify-between">
      <span className="font-display text-4xl sm:text-5xl text-yellow-400 leading-none">{big}</span>
      <span className="text-xs uppercase tracking-widest text-zinc-400 text-right max-w-[50%]">{label}</span>
    </div>
  );
}
